// "Needs review": what the iPads sent back that the office should check. Walk-ins (merge into someone
// already on file, or keep as a new person) and duplicate captures (void the wrong one, or keep both).
import type { AssessmentResult } from '@ngf/calc-engine';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { Measurements } from '../components/Intake.tsx';
import { ErrorBox } from '../components/Layout.tsx';
import { get, post } from '../lib/api.ts';
import { useData } from '../lib/useData.ts';

interface Person { id: string; firstName: string; lastName: string; dob: string | null; sex: string | null; employeeId: string | null; departmentName: string | null }
interface Match extends Person { lastAssessedOn: string | null; assessmentCount: number; score: number; reasons: string[] }
interface Walkin extends Person { createdAt: string; assessments: { id: string; assessedOn: string; status: string }[]; matches: Match[] }
interface DupAssessment {
  id: string; status: string; assessedOn: string; createdAt: string; capturedAt: string | null; fromIpad: boolean; flagged: boolean;
  createdByName: string | null; measurements: Measurements; results: AssessmentResult | null;
}
interface DupGroup { person: Person; event: { id: string; startsOn: string; location: string | null; departmentName: string } | null; assessments: DupAssessment[] }

const name = (p: { firstName: string; lastName: string }) => `${p.lastName}, ${p.firstName}`;
const sexText = (s: string | null) => (s === 'M' ? 'Male' : s === 'F' ? 'Female' : '—');
const when = (iso: string) => new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

export function useReviewCount() {
  const { data, reload } = useData<{ walkins: number; duplicates: number; rosters: number }>('/api/review/summary');
  const { pathname } = useLocation();
  useEffect(() => { void reload(); }, [pathname, reload]);
  useEffect(() => {
    const on = () => void reload();
    window.addEventListener('ngf-review-changed', on);
    return () => window.removeEventListener('ngf-review-changed', on);
  }, [reload]);
  return data ? data.walkins + data.duplicates + data.rosters : 0;
}

export function ReviewQueue() {
  const walkins = useData<Walkin[]>('/api/review/walkins');
  const dups = useData<DupGroup[]>('/api/review/duplicates');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const run = async (fn: () => Promise<string>) => {
    setErr(null); setMsg(null);
    try {
      setMsg(await fn());
      await Promise.all([walkins.reload(), dups.reload()]);
      window.dispatchEvent(new Event('ngf-review-changed'));   // the menu badge
    } catch (x) { setErr(x); }
  };

  const merge = (w: Walkin, into: Person) => {
    if (!window.confirm(`Merge the walk-in "${name(w)}" into ${name(into)}${into.dob ? ` (DOB ${into.dob})` : ''}?\n\n`
      + `Their ${w.assessments.length} assessment(s) and any documents move to ${name(into)}, who keeps their own name and details `
      + `(blanks are filled in from the walk-in). The walk-in record is retired, not deleted.`)) return;
    void run(async () => {
      const r = await post<{ movedAssessments: number; movedDocuments: number; flaggedDuplicates: number }>(`/api/review/walkins/${w.id}/merge`, { intoId: into.id });
      return `Merged ${name(w)} into ${name(into)}: ${r.movedAssessments} assessment(s), ${r.movedDocuments} document(s) moved.`
        + (r.flaggedDuplicates ? ` ${r.flaggedDuplicates} now overlap another assessment at the same event — see below.` : '');
    });
  };
  const keep = (w: Walkin) => void run(async () => { await post(`/api/review/walkins/${w.id}/keep`); return `${name(w)} kept as a new person.`; });
  const voidOne = (g: DupGroup, a: DupAssessment) => {
    const reason = window.prompt(`Void the ${a.fromIpad ? 'iPad' : 'office'} capture of ${name(g.person)} made ${when(a.capturedAt ?? a.createdAt)}?\n`
      + 'It is kept for the record but hidden from lists and can no longer change.\n\nReason:', 'Duplicate of another capture');
    if (!reason) return;
    void run(async () => { await post(`/api/review/assessments/${a.id}/void`, { reason }); return 'Assessment voided.'; });
  };
  const keepBoth = (g: DupGroup) => void run(async () => { await post(`/api/review/assessments/${g.assessments[0]!.id}/keep`); return `Kept all ${g.assessments.length} for ${name(g.person)}.`; });

  const nW = walkins.data?.length ?? 0, nD = dups.data?.length ?? 0;
  return (
    <>
      <h1>Needs review</h1>
      <p className="muted">Rosters sent in through organization portals, and walk-ins and possible duplicates sent back from iPad field mode. Nothing here is ever deleted: merged walk-ins are retired and voided records are kept.</p>
      <RosterSubmissions onChange={() => window.dispatchEvent(new Event('ngf-review-changed'))} />
      <ErrorBox error={walkins.error ?? dups.error ?? err} />
      {msg && <div className="alert ok">{msg}</div>}

      <h2>Walk-ins added on the iPad {walkins.data && <span className="badge">{nW}</span>}</h2>
      {!walkins.data ? <p className="muted">Loading…</p> : !nW ? <div className="card muted">Nothing to review.</div> : walkins.data.map((w) => (
        <div key={w.id} className="card review-card">
          <div className="spread">
            <div>
              <h3 style={{ margin: 0 }}><Link to={`/participants/${w.id}`}>{name(w)}</Link> <span className="badge">walk-in</span></h3>
              <div className="muted small">DOB {w.dob ?? '—'} · {sexText(w.sex)} · ID # {w.employeeId ?? '—'} · {w.departmentName ?? 'No organization'} · added {when(w.createdAt)}
                {' · '}{w.assessments.length} assessment{w.assessments.length === 1 ? '' : 's'}
                {w.assessments.map((a) => <span key={a.id}> · <Link to={`/assessments/${a.id}`}>{a.assessedOn}</Link></span>)}</div>
            </div>
            <button className="secondary" onClick={() => keep(w)}>Keep as a new person</button>
          </div>
          <h4 className="small" style={{ margin: '0.75rem 0 0.25rem' }}>{w.matches.length ? 'Possibly already on file' : 'No likely match on file'}</h4>
          {w.matches.length > 0 && <MatchTable rows={w.matches} onMerge={(p) => merge(w, p)} />}
          <FindPerson exclude={w.id} onPick={(p) => merge(w, p)} />
        </div>
      ))}

      <h2 style={{ marginTop: '1.5rem' }}>Possible duplicate captures {dups.data && <span className="badge">{nD}</span>}</h2>
      {!dups.data ? <p className="muted">Loading…</p> : !nD ? <div className="card muted">Nothing to review.</div> : dups.data.map((g) => (
        <div key={g.assessments[0]?.id ?? g.person.id} className="card review-card">
          <div className="spread">
            <div>
              <h3 style={{ margin: 0 }}><Link to={`/participants/${g.person.id}`}>{name(g.person)}</Link></h3>
              <div className="muted small">DOB {g.person.dob ?? '—'} · {g.event ? <>{g.event.departmentName} event, <Link to={`/events/${g.event.id}`}>{g.event.startsOn}</Link>{g.event.location ? ` · ${g.event.location}` : ''}</> : 'no event'}
                {' · '}{g.assessments.length} records for the same person and event</div>
            </div>
            <button className="secondary" onClick={() => keepBoth(g)}>Keep all — both are real</button>
          </div>
          <div className="table-scroll">
            <table className="compare" style={{ marginTop: '0.5rem' }}>
              <thead><tr><th>Captured</th><th>Status</th><th>Weight</th><th>BP</th><th>Chol / HDL</th><th>VO₂max</th><th>Risk</th><th>Body fat</th><th /></tr></thead>
              <tbody>{g.assessments.map((a) => {
                const m = a.measurements, r = a.results;
                const locked = a.status === 'signed' || a.status === 'released';
                return (
                  <tr key={a.id} className={a.flagged ? 'flagged' : ''}>
                    <td><Link to={`/assessments/${a.id}`}>{when(a.capturedAt ?? a.createdAt)}</Link>
                      <div className="muted small">{a.fromIpad ? 'iPad' : 'office'}{a.createdByName ? ` · ${a.createdByName}` : ''}{a.flagged ? ' · newer' : ''}</div></td>
                    <td><span className={`badge ${a.status}`}>{a.status}</span></td>
                    <td>{m.weightLb ?? '—'}</td>
                    <td>{m.restSbp ? `${m.restSbp}/${m.restDbp ?? '—'}` : '—'}</td>
                    <td>{m.totalChol ? `${m.totalChol}/${m.hdl ?? '—'}` : '—'}</td>
                    <td>{r?.aerobic?.vo2maxDisplayed ?? '—'}</td>
                    <td>{r?.coronaryRisk?.score ?? '—'}</td>
                    <td>{r?.bodyComposition ? `${r.bodyComposition.fatPct}%` : '—'}</td>
                    <td>{locked ? <span className="muted small">{a.status} — use a correction</span>
                      : <button className="link" onClick={() => voidOne(g, a)}>Void</button>}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}

function MatchTable({ rows, onMerge }: { rows: (Person & Partial<Match>)[]; onMerge: (p: Person) => void }) {
  return (
    <div className="table-scroll">
      <table>
        <thead><tr><th>Name</th><th>DOB</th><th>Sex</th><th>ID #</th><th>Organization</th><th>Last assessed</th><th>Why suggested</th><th /></tr></thead>
        <tbody>{rows.map((p) => (
          <tr key={p.id}>
            <td><Link to={`/participants/${p.id}`}>{name(p)}</Link></td><td>{p.dob ?? '—'}</td><td>{sexText(p.sex)}</td><td>{p.employeeId ?? '—'}</td>
            <td>{p.departmentName ?? '—'}</td><td>{p.lastAssessedOn ?? '—'}{p.assessmentCount ? <span className="muted small"> ({p.assessmentCount})</span> : null}</td>
            <td className="small">{p.reasons?.join(', ') ?? 'picked by search'}</td>
            <td><button onClick={() => onMerge(p)}>Merge into this person</button></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

/** Not in the suggestions? Search the whole roster. */
function FindPerson({ exclude, onPick }: { exclude: string; onPick: (p: Person) => void }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Person[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const search = async () => {
    setErr(null);
    try { setRows((await get<Person[]>(`/api/participants?limit=10&q=${encodeURIComponent(q.trim())}`)).filter((p) => p.id !== exclude)); }
    catch (x) { setErr(x); }
  };
  return (
    <details className="find-person">
      <summary className="small">Search for someone else to merge into</summary>
      <form className="row" onSubmit={(e) => { e.preventDefault(); if (q.trim()) void search(); }} style={{ marginTop: '0.5rem' }}>
        <div><input aria-label="Search by name or employee ID" placeholder="Name or employee ID" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <div style={{ flex: '0 0 auto' }}><button className="secondary">Search</button></div>
      </form>
      <ErrorBox error={err} />
      {rows && (rows.length ? <MatchTable rows={rows.map((p) => ({ ...p, lastAssessedOn: (p as Match).lastAssessedOn ?? null }))} onMerge={onPick} />
        : <p className="muted small">No one found.</p>)}
    </details>
  );
}

// ---------------------------------------------------------------- rosters sent in through the portal
interface Submission {
  id: string; departmentId: string; departmentName: string; submittedAt: string; filename: string | null; note: string | null; rowCount: number;
  submittedBy: string; submittedByEmail: string; events: { id: string; startsOn: string; location: string | null; status: string }[];
  preview: Record<string, string>[];
}

function RosterSubmissions({ onChange }: { onChange: () => void }) {
  const subs = useData<Submission[]>('/api/review/rosters');
  const [target, setTarget] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const act = async (fn: () => Promise<string>) => {
    setErr(null); setMsg(null);
    try { setMsg(await fn()); await subs.reload(); onChange(); } catch (x) { setErr(x); }
  };
  const importIt = (s: Submission) => {
    const eventId = target[s.id] ?? s.events[0]?.id;
    if (!eventId) return;
    void act(async () => {
      const r = await post<{ added: number; updated: number; problems: { row: number; problem: string }[] }>(`/api/review/rosters/${s.id}/import`, { eventId });
      return `${s.departmentName}: ${r.added} added, ${r.updated} updated${r.problems.length ? `, ${r.problems.length} rows skipped (${r.problems.slice(0, 3).map((p) => `row ${p.row}: ${p.problem}`).join('; ')})` : ''}.`;
    });
  };
  const dismiss = (s: Submission) => {
    const reason = window.prompt(`Do not use this roster from ${s.departmentName}? The contact will see it as "Not used".

Reason:`);
    if (reason) void act(async () => { await post(`/api/review/rosters/${s.id}/dismiss`, { reason }); return 'Roster set aside.'; });
  };
  if (!subs.data?.length && !msg && !err) return null;
  return (
    <>
      <h2>Rosters sent in by organizations {subs.data && <span className="badge">{subs.data.length}</span>}</h2>
      <ErrorBox error={subs.error ?? err} />
      {msg && <div className="alert ok">{msg}</div>}
      {subs.data?.map((s) => (
        <div key={s.id} className="card review-card">
          <div className="spread">
            <div>
              <h3 style={{ margin: 0 }}><Link to={`/organizations/${s.departmentId}`}>{s.departmentName}</Link></h3>
              <div className="muted small">{s.rowCount} {s.rowCount === 1 ? 'person' : 'people'} · {s.filename ?? 'roster.csv'} · sent {when(s.submittedAt)} by {s.submittedBy} ({s.submittedByEmail})</div>
              {s.note && <div className="small" style={{ marginTop: 4 }}>Note: {s.note}</div>}
            </div>
            <button className="link" onClick={() => dismiss(s)}>Don't use</button>
          </div>
          {s.preview.length > 0 && <div className="small muted" style={{ marginTop: 6 }}>First rows: {s.preview.map((p) => `${p.lastName}, ${p.firstName}${p.dob ? ` (${p.dob})` : ''}`).join(' · ')}</div>}
          {s.events.length ? (
            <div className="row" style={{ marginTop: '0.75rem' }}>
              <div><label htmlFor={`ev-${s.id}`}>Add to event</label>
                <select id={`ev-${s.id}`} value={target[s.id] ?? s.events[0]!.id} onChange={(e) => setTarget({ ...target, [s.id]: e.target.value })}>
                  {s.events.map((e) => <option key={e.id} value={e.id}>{e.startsOn}{e.location ? ` · ${e.location}` : ''} ({e.status})</option>)}
                </select></div>
              <div style={{ flex: '0 0 auto' }}><button onClick={() => importIt(s)}>Import roster</button></div>
            </div>
          ) : <p className="small" style={{ marginTop: '0.75rem' }}>No upcoming event for {s.departmentName}. <Link to="/events">Schedule one</Link>, then import this roster into it.</p>}
        </div>
      ))}
    </>
  );
}
